import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography
} from "@mui/material";
import { useState } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { downloadDepthMeasurementsTemplateFile } from "../../../api/imports";
import { DepthMeasurementsConfirmImportDialog } from "./DepthMeasurementsConfirmImportDialog";

interface DepthMeasurementsImportDialogProps {
  captureId: number,
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const DepthMeasurementsImportDialog = ({
  captureId,
  open, 
  onClose,
  onSuccess,
}: DepthMeasurementsImportDialogProps) => {
  const [files, setFiles] = useState<File[]>([]);
  const [openConfirm, setOpenConfirm] = useState(false);
  const { t } = useTranslation();
  const filename = "depth_measurements_template.xlsx";

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (accepted) => setFiles(accepted),
    accept: {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "text/csv": [".csv"],
    },
    multiple: false,
  });

  return (
    <Box sx={{ width: "100%" }}>
      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (reason === "backdropClick") return;
          onClose();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("imports.import-dialog.title")}</DialogTitle>

        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 1 }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Button
              variant="outlined"
              sx={{ width: "50%" }}
              onClick={() =>
                downloadDepthMeasurementsTemplateFile(filename, captureId)
              }
            >
              {t("imports.import-dialog.download-button")}
            </Button>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1, textAlign: "center", maxWidth: "80%" }}
            >
              {t("imports.import-dialog.download-instructions")}
            </Typography>
          </Box>

          <Box
            {...getRootProps()}
            sx={{
              border: isDragActive
                ? "2px solid primary.main"
                : "2px dashed gray",
              borderRadius: 2,
              p: 6,
              textAlign: "center",
              cursor: "pointer",
              transition: "all 0.2s",
              backgroundColor: isDragActive
                ? "action.hover"
                : "background.paper",
              boxShadow: isDragActive ? 3 : 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.5,
            }}
          >
            <input {...getInputProps()} />
            <CloudUploadIcon
              sx={{
                fontSize: 50,
                mb: 1,
                color: isDragActive ? "primary.main" : "gray",
              }}
            />
            <Typography>{t("imports.import-dialog.drag-and-drop")}</Typography>
          </Box>

          {files.length > 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                p: 1,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                backgroundColor: "background.default",
                boxShadow: 1,
              }}
            >
              <Typography>
                {t("imports.import-dialog.selected-file")}:{" "}
                <b>{files[0].name}</b>
              </Typography>

              <Tooltip title={t("imports.import-dialog.remove-file")}>
                <IconButton color="error" onClick={() => setFiles([])}>
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ justifyContent: "space-between", px: 3, py: 2 }}>
          <Button onClick={onClose}>{t("generic.cancel")}</Button>
          <Button
            variant="contained"
            disabled={files.length === 0}
            onClick={() => setOpenConfirm(true)}
          >
            {t("generic.continue")}
          </Button>
        </DialogActions>
      </Dialog>

      <DepthMeasurementsConfirmImportDialog
        captureId={captureId}
        open={openConfirm}
        file={files[0]}
        onClose={() => setOpenConfirm(false)}
        onSuccess={() => {
          setFiles([]);
          setOpenConfirm(false);
          onSuccess();
        }}
      />
    </Box>
  );
};